/**
 * 계약관리 — 계약대장 등록 (Phase A)
 * - 물품·용역 · 조달채널 다중(extRef) · 원인행위 N:M 링크 조회
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  CollapsibleSectionPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  CONTRACT_OBJECT_PRIMARY_OPTIONS,
  CONTRACT_STATUS_OPTIONS,
  PROCUREMENT_CHANNEL_OPTIONS,
  AWARD_METHOD_OPTIONS,
  CONTRACT_STATUS,
  CONTRACT_OBJECT,
  PROCUREMENT_CHANNEL,
  AWARD_METHOD,
  emptyContract,
  emptyExtRef,
  enrichContractRow,
  filterContracts,
  loadContractBundle,
  saveContractBundle,
  ensureContractSeed,
  linksOfContract,
  extRefsOfContract,
  createContractUid,
  createExtRefUid,
  todayYmd,
} from 'contract/contractStore';

const LIST_GRID_ID = 'contract-ledger-list';
const EXT_GRID_ID = 'contract-ledger-ext';
const LINK_GRID_ID = 'contract-ledger-link';
const MASTER_EXPORT_ID = 'contract-ledger-master';
const LIST_PANEL_H = '300px';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || '0 0 auto'};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  height: ${(p) => p.$height || '180px'};
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

const Title = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  padding: 6px 10px;
  border-bottom: 1px solid ${theme.colors.border};
`;

function pickSearch(v) {
  return {
    searchFiscalYear: v.searchFiscalYear || '',
    searchObjectType: v.searchObjectType || '',
    searchStatus: v.searchStatus || '',
    searchChannel: v.searchChannel || '',
    searchKeyword: v.searchKeyword || '',
  };
}

function ContractRegisterInner() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const listRef = useRef(null);
  const extRef = useRef(null);
  const linkRef = useRef(null);
  const selectedUidRef = useRef('');
  const [selectedUid, setSelectedUid] = useState('');
  const [bundle, setBundle] = useState({ contracts: [], oblLinks: [], extRefs: [] });
  const [listCount, setListCount] = useState(0);

  const methods = useForm({
    defaultValues: {
      searchFiscalYear: getCurrentFiscalYear(),
      searchObjectType: '',
      searchStatus: '',
      searchChannel: '',
      searchKeyword: '',
      ...emptyContract({
        fiscalYear: getCurrentFiscalYear(),
        status: CONTRACT_STATUS.SIGNED,
        objectType: CONTRACT_OBJECT.GOODS,
        primaryChannel: PROCUREMENT_CHANNEL.INTERNAL,
        awardMethod: AWARD_METHOD.OTHER,
        registerDate: todayYmd(),
        contractDate: todayYmd(),
      }),
    },
  });
  const { control, getValues, reset, setValue } = methods;
  const fyWatch = useWatch({ control, name: 'searchFiscalYear' });

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (id === EXT_GRID_ID && !selectedUidRef.current) return '계약을 선택하거나 추가하세요.';
      if (id === LINK_GRID_ID) return '원인행위 연결은 원인행위 화면에서 생성·불러오기 하세요.';
      return null;
    },
  });

  const dims = useCallback(() => {
    const v = getValues();
    return {
      tenantCode,
      fiscalYear: String(v.searchFiscalYear || '').trim(),
    };
  }, [getValues, tenantCode]);

  const loadExtAndLinks = useCallback(
    (uid, b = bundle) => {
      const links = linksOfContract(b.oblLinks, uid).map((l) => ({
        ...l,
        id: l.linkUid,
      }));
      const exts = extRefsOfContract(b.extRefs, uid).map((e) => ({
        ...e,
        id: e.extRefUid,
        channelNm: PROCUREMENT_CHANNEL_OPTIONS.find((o) => o.value === e.channel)?.label || e.channel,
      }));
      linkRef.current?.setData?.(links);
      extRef.current?.setData?.(exts);
    },
    [bundle]
  );

  const loadMaster = useCallback(
    (row) => {
      const searchKeep = pickSearch(getValues());
      if (!row) {
        reset({
          ...searchKeep,
          ...emptyContract({
            fiscalYear: searchKeep.searchFiscalYear || getCurrentFiscalYear(),
            status: CONTRACT_STATUS.SIGNED,
            objectType: CONTRACT_OBJECT.GOODS,
            registerDate: todayYmd(),
            contractDate: todayYmd(),
          }),
        });
        selectedUidRef.current = '';
        setSelectedUid('');
        linkRef.current?.setData?.([]);
        extRef.current?.setData?.([]);
        return;
      }
      selectedUidRef.current = row.contractUid;
      setSelectedUid(row.contractUid);
      reset({ ...searchKeep, ...row });
      loadExtAndLinks(row.contractUid);
    },
    [getValues, reset, loadExtAndLinks]
  );

  const handleSearch = useCallback(async () => {
    const { fiscalYear } = dims();
    if (!fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    await ensureContractSeed(tenantCode, fiscalYear);
    const b = await loadContractBundle(tenantCode, fiscalYear);
    setBundle(b);
    const v = getValues();
    const list = filterContracts(b.contracts, {
      objectType: v.searchObjectType,
      status: v.searchStatus,
      primaryChannel: v.searchChannel,
      keyword: v.searchKeyword,
      primaryOnly: true,
    }).map(enrichContractRow);
    listRef.current?.setData?.(list);
    setListCount(list.length);
    const first = list[0];
    loadMaster(first);
  }, [dims, tenantCode, getValues, loadMaster]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fyWatch]);

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '계약번호', field: 'contractNo', size: 110 }),
      hiCol.roTextColumn({ header: '계약명', field: 'contractName', size: 160 }),
      hiCol.roTextColumn({ header: '목적물', field: 'objectTypeNm', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '채널', field: 'primaryChannelNm', size: 100 }),
      hiCol.roTextColumn({ header: '상대방', field: 'vendorName', size: 120 }),
      hiCol.roNumberColumn({ header: '계약금액', field: 'contractAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '계약일', field: 'contractDate', size: 100, align: 'center' }),
    ],
    []
  );

  const extColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '조달채널',
        field: 'channel',
        size: 130,
        options: PROCUREMENT_CHANNEL_OPTIONS,
      }),
      hiCol.edTextColumn({ header: '공고번호', field: 'noticeNo', size: 130 }),
      hiCol.edTextColumn({ header: '외부계약번호', field: 'extContractNo', size: 140 }),
      hiCol.edTextColumn({ header: '동기일', field: 'syncedAt', size: 100 }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 120 }),
    ],
    []
  );

  const linkColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '원인행위번호', field: 'obligationNo', size: 110 }),
      hiCol.roTextColumn({ header: '원인행위UID', field: 'obligationUid', size: 160 }),
      hiCol.roNumberColumn({ header: '연결금액', field: 'linkedAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 140 }),
    ],
    []
  );

  const listGrid = useHiGridHook({
    gridRef: listRef,
    columns: listColumns,
    enableContextMenu: false,
  });

  const extGrid = useHiGridHook({
    gridRef: extRef,
    columns: extColumns,
    enableContextMenu: true,
    defaultRow: emptyExtRef(),
  });

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => loadMaster(data);
    listGrid.onSelectRow = ({ data }) => loadMaster(data);
  }, [listGrid, loadMaster]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'searchFiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          labelWidth: SEARCH_LABEL_W,
          width: '88px',
        },
        {
          name: 'searchObjectType',
          label: '목적물',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...CONTRACT_OBJECT_PRIMARY_OPTIONS],
          labelWidth: SEARCH_LABEL_W,
          width: '120px',
        },
        {
          name: 'searchStatus',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...CONTRACT_STATUS_OPTIONS],
          labelWidth: SEARCH_LABEL_W,
          width: '120px',
        },
        {
          name: 'searchChannel',
          label: '채널',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...PROCUREMENT_CHANNEL_OPTIONS],
          labelWidth: SEARCH_LABEL_W,
          width: '140px',
        },
        {
          name: 'searchKeyword',
          label: '검색',
          type: 'text',
          placeholder: '번호·명·상대방',
          labelWidth: SEARCH_LABEL_W,
          width: '200px',
        },
      ],
    ],
    []
  );

  const masterFields = useMemo(
    () => [
      [
        { name: 'contractNo', label: '계약번호', type: 'text', labelWidth: 72, width: '120px' },
        { name: 'contractName', label: '계약명', type: 'text', labelWidth: 72, width: '240px' },
        {
          name: 'objectType',
          label: '목적물',
          type: 'select',
          options: CONTRACT_OBJECT_PRIMARY_OPTIONS,
          labelWidth: 72,
          width: '110px',
        },
        {
          name: 'status',
          label: '상태',
          type: 'select',
          options: CONTRACT_STATUS_OPTIONS,
          labelWidth: 72,
          width: '110px',
        },
      ],
      [
        {
          name: 'primaryChannel',
          label: '대표채널',
          type: 'select',
          options: PROCUREMENT_CHANNEL_OPTIONS,
          labelWidth: 72,
          width: '140px',
        },
        {
          name: 'awardMethod',
          label: '낙찰방법',
          type: 'select',
          options: AWARD_METHOD_OPTIONS,
          labelWidth: 72,
          width: '130px',
        },
        { name: 'contractDate', label: '계약일', type: 'date', labelWidth: 72, width: '130px' },
        { name: 'startDate', label: '시작일', type: 'date', labelWidth: 72, width: '130px' },
        { name: 'endDate', label: '종료일', type: 'date', labelWidth: 72, width: '130px' },
        { name: 'contractAmt', label: '계약금액', type: 'number', labelWidth: 72, width: '120px' },
      ],
      [
        { name: 'vendorName', label: '상대방', type: 'text', labelWidth: 72, width: '160px' },
        { name: 'businessNo', label: '사업자번호', type: 'text', labelWidth: 72, width: '140px' },
        { name: 'deptName', label: '부서', type: 'text', labelWidth: 72, width: '120px' },
        { name: 'projectName', label: '사업', type: 'text', labelWidth: 72, width: '140px' },
        { name: 'subjectName', label: '과목', type: 'text', labelWidth: 72, width: '140px' },
        {
          name: 'socialValueYn',
          label: '사회적가치',
          type: 'select',
          options: [
            { value: 'Y', label: 'Y' },
            { value: 'N', label: 'N' },
          ],
          labelWidth: 72,
          width: '88px',
        },
      ],
      [{ name: 'remark', label: '비고', type: 'text', labelWidth: 72, width: '480px' }],
    ],
    []
  );

  const handleNew = useCallback(() => {
    const fy = dims().fiscalYear || getCurrentFiscalYear();
    const uid = createContractUid();
    selectedUidRef.current = uid;
    setSelectedUid(uid);
    reset({
      ...pickSearch(getValues()),
      ...emptyContract({
        contractUid: uid,
        fiscalYear: fy,
        status: CONTRACT_STATUS.SIGNED,
        objectType: CONTRACT_OBJECT.GOODS,
        primaryChannel: PROCUREMENT_CHANNEL.INTERNAL,
        registerDate: todayYmd(),
        contractDate: todayYmd(),
        mod: 'A',
      }),
    });
    extRef.current?.setData?.([]);
    linkRef.current?.setData?.([]);
  }, [dims, getValues, reset]);

  const collectExts = useCallback(() => {
    const uid = selectedUidRef.current;
    return (extRef.current?.getData?.() || [])
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...emptyExtRef(r),
        extRefUid: r.extRefUid || createExtRefUid(),
        contractUid: uid,
        mod: r.mod === 'A' ? 'A' : 'U',
      }));
  }, []);

  const handleSave = useCallback(async () => {
    const { fiscalYear } = dims();
    if (!fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    let uid = selectedUidRef.current;
    if (!uid) {
      uid = createContractUid();
      selectedUidRef.current = uid;
      setSelectedUid(uid);
      setValue('contractUid', uid);
    }
    const form = getValues();
    if (!String(form.contractNo || '').trim()) {
      showAlert('계약번호를 입력하세요.');
      return;
    }
    if (!CONTRACT_OBJECT_PRIMARY_OPTIONS.some((o) => o.value === form.objectType)) {
      showAlert('1차 목적물은 물품·용역만 등록할 수 있습니다.');
      return;
    }

    const b = await loadContractBundle(tenantCode, fiscalYear);
    const master = emptyContract({
      ...form,
      contractUid: uid,
      fiscalYear,
      registerDate: form.registerDate || todayYmd(),
      mod: 'U',
    });

    let contracts = [...(b.contracts || [])];
    const idx = contracts.findIndex((c) => c.contractUid === uid);
    if (idx >= 0) contracts[idx] = { ...contracts[idx], ...master, mod: 'U' };
    else contracts.push({ ...master, mod: 'A' });

    let extRefs = (b.extRefs || []).filter((e) => e.contractUid !== uid);
    extRefs = [...extRefs, ...collectExts()];

    const saved = await saveContractBundle(tenantCode, fiscalYear, {
      contracts,
      oblLinks: b.oblLinks,
      extRefs,
    });
    setBundle(saved);
    await handleSearch();
    const row = saved.contracts.find((c) => c.contractUid === uid);
    loadMaster(enrichContractRow(row));
    showAlert('저장했습니다.', 'success');
  }, [dims, tenantCode, getValues, setValue, collectExts, handleSearch, loadMaster]);

  const handleDelete = useCallback(async () => {
    if (activeGridId === EXT_GRID_ID) {
      await handleActiveDelete();
      return;
    }
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('삭제할 계약을 선택하세요.');
      return;
    }
    const ok = await showConfirm('선택한 계약을 삭제할까요? (원인행위 링크는 해제되지 않고 조회에서만 제외)');
    if (!ok) return;
    const { fiscalYear } = dims();
    const b = await loadContractBundle(tenantCode, fiscalYear);
    const contracts = (b.contracts || []).map((c) =>
      c.contractUid === uid ? { ...c, mod: 'D' } : c
    );
    await saveContractBundle(tenantCode, fiscalYear, {
      contracts,
      oblLinks: b.oblLinks,
      extRefs: b.extRefs,
    });
    await handleSearch();
    showAlert('삭제했습니다.', 'success');
  }, [activeGridId, handleActiveDelete, dims, tenantCode, handleSearch]);

  const dispatchExtAdd = useCallback(async () => {
    if (!selectedUidRef.current) return;
    extRef.current?.setData?.([
      ...(extRef.current?.getData?.() || []),
      emptyExtRef({
        extRefUid: createExtRefUid(),
        contractUid: selectedUidRef.current,
        channel: PROCUREMENT_CHANNEL.G2B,
        syncedAt: todayYmd(),
        mod: 'A',
      }),
    ]);
  }, []);

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '계약대장',
  });

  useRegisterGridExport({
    id: MASTER_EXPORT_ID,
    label: '계약마스터',
    getData: () => {
      const v = getValues();
      return v.contractUid ? [enrichContractRow(v)] : [];
    },
    order: 0,
  });
  useRegisterGridExport({
    id: LIST_GRID_ID,
    label: '계약목록',
    getData: () => listRef.current?.getData?.() || [],
    order: 1,
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      { id: 'new', label: '추가', icon: '➕', onClick: handleNew, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel}` : '삭제 (F9)',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleNew, handleDelete, handleSave, openExcelDialog, activeLabel]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <Stack>
          <CollapsibleSearchPanel storageKey="contract-ledger" prefsUserId={prefsUserId} title="조회조건">
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            1차: 물품·용역 · 조달채널은 대표채널 + 외부키 그리드(나라장터·계약마당 등 병행) · 원인행위는
            N:M 링크로 조회(생성은 원인행위 화면)
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
          </Hint>

          <Panel>
            <GridExportFocusScope gridId={MASTER_EXPORT_ID}>
              <Title>계약 마스터</Title>
              <FormArea dense fields={masterFields} />
            </GridExportFocusScope>
          </Panel>

          <Panel>
            <ActiveGridScope
              gridId={EXT_GRID_ID}
              label="조달채널외부키"
              order={10}
              onAdd={dispatchExtAdd}
              onDelete={async () => extGrid.handleDelete?.()}
              canAdd
              canDelete
            >
              <ActiveGridHeader gridId={EXT_GRID_ID}>조달채널 외부키 (다채널)</ActiveGridHeader>
              <GridBody $height="160px">
                <HiGridComponent
                  ref={extRef}
                  columns={extColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableContextMenu
                  enableRowEditMenu
                  enableHeaderMenu
                  gridId="contract-ledger-ext"
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === EXT_GRID_ID}
                  {...(extGrid.contextMenuProps || {})}
                />
              </GridBody>
            </ActiveGridScope>
          </Panel>

          <Panel>
            <ActiveGridScope
              gridId={LINK_GRID_ID}
              label="원인행위연결"
              order={20}
              onAdd={async () => {}}
              onDelete={async () => {}}
              canAdd={false}
              canDelete={false}
            >
              <ActiveGridHeader gridId={LINK_GRID_ID}>원인행위 연결 (N:M · 조회)</ActiveGridHeader>
              <GridBody $height="140px">
                <HiGridComponent
                  ref={linkRef}
                  columns={linkColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableHeaderMenu
                  gridId="contract-ledger-link"
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === LINK_GRID_ID}
                />
              </GridBody>
            </ActiveGridScope>
          </Panel>

          <CollapsibleSectionPanel
            storageKey="contract-ledger-list"
            prefsUserId={prefsUserId}
            title="계약 목록"
            summary={listCount ? `${listCount}건` : '조회 후 표시'}
            defaultCollapsed={false}
            expandedHeight={LIST_PANEL_H}
          >
            <ActiveGridScope
              gridId={LIST_GRID_ID}
              label="계약목록"
              order={30}
              onAdd={handleNew}
              onDelete={handleDelete}
              canAdd
              canDelete
            >
              <GridBody $height={LIST_PANEL_H}>
                <HiGridComponent
                  ref={listRef}
                  columns={listColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableHeaderMenu
                  enableQuickExport
                  exportFileName="계약대장목록"
                  gridId="contract-ledger-list"
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === LIST_GRID_ID}
                />
              </GridBody>
            </ActiveGridScope>
          </CollapsibleSectionPanel>
        </Stack>
      </FormProvider>
      {excelDialog}
    </PageContainer>
  );
}

export default function ContractRegister() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <ContractRegisterInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
